import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Users, DollarSign, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
import SecurityCheck from '../components/SecurityCheck';

const AffiliateLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [securityVerified, setSecurityVerified] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // If already logged in, redirect to affiliate portal
  useEffect(() => {
    if (user) {
      navigate('/affiliates');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome to the Affiliate Portal!');
      navigate('/affiliates');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Show security check first
  if (!securityVerified) {
    return <SecurityCheck onVerified={() => setSecurityVerified(true)} />;
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] flex" data-testid="affiliate-login">
      {/* Left Side - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[hsl(220,25%,8%)] to-[hsl(220,20%,4%)] p-12 flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center gap-3 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <Users className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-white">Affiliate Program</h1>
              <p className="text-cyan-400">TRADERSCAPITAL</p>
            </div>
          </div>

          <h2 className="text-4xl font-heading font-bold text-white mb-6">
            Earn <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">5% Commission</span> on Every Referral
          </h2>
          
          <p className="text-gray-400 text-lg mb-10">
            Join our affiliate program and earn passive income by referring traders to TRADERSCAPITAL. 
            Get paid for every first-time purchase your referrals make.
          </p>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="font-bold text-white mb-1">Competitive Commission</h3>
                <p className="text-gray-400 text-sm">Earn 5% on every first-time referral purchase</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h3 className="font-bold text-white mb-1">Real-Time Tracking</h3>
                <p className="text-gray-400 text-sm">Monitor your referrals and earnings in real-time</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-bold text-white mb-1">Fast Payouts</h3>
                <p className="text-gray-400 text-sm">Withdraw your earnings via Bank, PayPal, or Crypto</p>
              </div>
            </div>
          </div>

          <div className="mt-12 p-6 rounded-2xl bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-cyan-500/20">
            <p className="text-cyan-400 font-medium mb-2">💰 Average Affiliate Earnings</p>
            <p className="text-3xl font-bold text-white font-mono">$2,500+<span className="text-lg text-gray-400">/month</span></p>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="bg-[hsl(220,20%,7%)] border-white/10 shadow-2xl">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Users className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl text-white">Affiliate Login</CardTitle>
              <CardDescription className="text-gray-400">
                Access your affiliate dashboard
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-300">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="affiliate-login-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-300">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                    data-testid="affiliate-login-password"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 font-heading uppercase tracking-wider"
                  disabled={loading}
                  data-testid="affiliate-login-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Users className="w-4 h-4 mr-2" />
                  )}
                  Access Portal
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-center text-gray-400 text-sm mb-4">
                  Not an affiliate yet?
                </p>
                <Link to="/register">
                  <Button 
                    variant="outline" 
                    className="w-full border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                  >
                    Create Account & Apply
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>

              <div className="mt-6 text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-gray-500 hover:text-cyan-400 transition-colors"
                >
                  Looking for Trader Login? Click here
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Mobile Benefits */}
          <div className="lg:hidden mt-8 p-6 rounded-2xl bg-[hsl(220,20%,7%)] border border-white/10">
            <h3 className="font-bold text-white mb-4">Why Join Our Affiliate Program?</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                5% commission on referrals
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Real-time tracking dashboard
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Multiple payout options
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AffiliateLogin;
